/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import { Font, create } from "@coretech/fontkit-lite";
import { TQUtils } from "../TQUtils";
import TQFont, { TQLogLevel, tqtypes } from "../../public/TQTypes";
import TQInitParams from "../../public/TQInitParam";

enum InternalFontScript {
    /** */
    kTQRomanScript = 0,
    /** */
    kTQJapaneseScript,
    /** */
    kTQTraditionalChineseScript,
    /** */
    kTQKoreanScript,
    /** */
    kTQArabicScript,
    /** */
    kTQHebrewScript,
    /** */
    kTQGreekScript,
    /** */
    kTQCyrillicScript,
    /** */
    kTQRightLeftScript,
    /** */
    kTQDevanagariScript,
    /** */
    kTQGurmukhiScript,
    /** */
    kTQGujaratiScript,
    /** */
    kTQOriyaScript,
    /** */
    kTQBengaliScript,
    /** */
    kTQTamilScript,
    /** */
    kTQTeluguScript,
    /** */
    kTQKannadaScript,
    /** */
    kTQMalayalamScript,
    /** */
    kTQSinhaleseScript,
    /** */
    kTQBurmeseScript,
    /** */
    kTQKhmerScript,
    /** */
    kTQThaiScript,
    /** */
    kTQLaotianScript,
    /** */
    kTQGeorgianScript,
    /** */
    kTQArmenianScript,
    /** */
    kTQSimplifiedChineseScript,
    /** */
    kTQTibetanScript,
    /** */
    kTQMongolianScript,
    /** */
    kTQGeezScript,
    /** */
    kTQEastEuropeanRomanScript,
    /** */
    kTQVietnameseScript,
    /** */
    kTQExtendedArabicScript,
    /** */
    kTQKlingonScript,
    /** */
    kTQEmojiScript,
    /** Count of above writing scripts (not a valid writing script value) */
    kTQScriptCount,
    /**
     Font script not known
     */
    kTQUnknownScript = 0x40000000,
}

function ConvertInternalFontScriptToLanguage(script: InternalFontScript) {
    switch (script) {
        // ============== Emoji ==================
        case InternalFontScript.kTQEmojiScript:
            return tqtypes.Language.Emoji;

        // ============== Japanese ==================
        case InternalFontScript.kTQJapaneseScript:
            return tqtypes.Language.Japanese;

        // ============== Chinese ==================
        case InternalFontScript.kTQTraditionalChineseScript:
            return tqtypes.Language.TraditionalChinese;

        case InternalFontScript.kTQSimplifiedChineseScript:
            return tqtypes.Language.SimplifiedChinese;

        // ============== Korean ==================
        case InternalFontScript.kTQKoreanScript:
            return tqtypes.Language.Korean;

        // ============== Arabic ==================
        case InternalFontScript.kTQArabicScript:
        case InternalFontScript.kTQExtendedArabicScript:
            return tqtypes.Language.Arabic;

        // ============== Hebrew ==================
        case InternalFontScript.kTQHebrewScript:
            return tqtypes.Language.Hebrew;

        // ============== INDIC ==================
        // Not existing ones have been replaced with Default
        case InternalFontScript.kTQDevanagariScript:
            return tqtypes.Language.Devanagari;

        case InternalFontScript.kTQGurmukhiScript:
            return tqtypes.Language.Gurmukhi;

        case InternalFontScript.kTQGujaratiScript:
            return tqtypes.Language.Gujarati;

        case InternalFontScript.kTQOriyaScript:
            // return tqtypes.Language.Oriya;
            return tqtypes.Language.Default;

        case InternalFontScript.kTQBengaliScript:
            return tqtypes.Language.Bengali;

        case InternalFontScript.kTQTamilScript:
            return tqtypes.Language.Tamil;
        /** */
        case InternalFontScript.kTQTeluguScript:
            return tqtypes.Language.Telugu;

        case InternalFontScript.kTQKannadaScript:
            return tqtypes.Language.Kannada;

        case InternalFontScript.kTQMalayalamScript:
            return tqtypes.Language.Malayalam;

        case InternalFontScript.kTQThaiScript:
            return tqtypes.Language.Thai;

        case InternalFontScript.kTQSinhaleseScript:
            // return tqtypes.Language.Sinhalese;
            return tqtypes.Language.Default;

        case InternalFontScript.kTQBurmeseScript:
            // return tqtypes.Language.Burmese;
            return tqtypes.Language.Default;

        case InternalFontScript.kTQKhmerScript:
            // return tqtypes.Language.Khmer;
            return tqtypes.Language.Default;

        // ============== Western ==================
        case InternalFontScript.kTQArmenianScript:
            return tqtypes.Language.Armenian;

        case InternalFontScript.kTQVietnameseScript:
            return tqtypes.Language.Vietnamese;

        case InternalFontScript.kTQGreekScript:
            return tqtypes.Language.Greek;

        case InternalFontScript.kTQTibetanScript:
        case InternalFontScript.kTQGeorgianScript:
        case InternalFontScript.kTQLaotianScript:
        case InternalFontScript.kTQMongolianScript:
        case InternalFontScript.kTQGeezScript:
        case InternalFontScript.kTQEastEuropeanRomanScript:
        case InternalFontScript.kTQKlingonScript:
        case InternalFontScript.kTQCyrillicScript:
        case InternalFontScript.kTQRightLeftScript:
        case InternalFontScript.kTQRomanScript:
            return tqtypes.Language.Default;
    }
    return tqtypes.Language.Default;
}

const kInt32Size = 4;

const gScriptUnicodeList: Map<InternalFontScript, number> = new Map([
    [InternalFontScript.kTQKoreanScript, 0xac00 /* HANGUL SYLLABLE KIYEOK A  */],
    [InternalFontScript.kTQJapaneseScript, 0x30a2 /* KATAKANA LETTER A  */],
    [InternalFontScript.kTQJapaneseScript, 0x3042 /* HIRAGANA LETTER A  */],
    [InternalFontScript.kTQArabicScript, 0x0622 /* afii57410 */],
    [InternalFontScript.kTQHebrewScript, 0x05d0 /* afii57664 */],
    [InternalFontScript.kTQEastEuropeanRomanScript, 0x010c /* Ccaron */],
    [InternalFontScript.kTQEastEuropeanRomanScript, 0x0148 /* ncaron */],
    [InternalFontScript.kTQEastEuropeanRomanScript, 0x0173 /* uogonek */],
    [InternalFontScript.kTQCyrillicScript, 0x042f /* afii10049 */],
    [InternalFontScript.kTQCyrillicScript, 0x0451 /* afii10071 */],
    [InternalFontScript.kTQGreekScript, 0x03cb /* upsilondieresis */],
    //Added entries for Indic Scripts
    [InternalFontScript.kTQKannadaScript, 0x0c85 /* KANNADA LETTER A  */],
    [InternalFontScript.kTQDevanagariScript, 0x0905 /* DEVANAGARI LETTER A  */],
    [InternalFontScript.kTQDevanagariScript, 0x090e /* DEVANAGARI LETTER ऎ */],
    [InternalFontScript.kTQGurmukhiScript, 0x0a05 /* GURMUKHI LETTER A  */],
    [InternalFontScript.kTQGujaratiScript, 0x0a85 /* GUJARATI LETTER A  */],
    [InternalFontScript.kTQBengaliScript, 0x0985 /* BENGALI LETTER A  */],
    [InternalFontScript.kTQMalayalamScript, 0x0d05 /* MALAYALAM LETTER A  */],
    [InternalFontScript.kTQTamilScript, 0x0b85 /* TAMIL LETTER A  */],
    [InternalFontScript.kTQTeluguScript, 0x0c05 /* TELUGU LETTER A  */],
    [InternalFontScript.kTQOriyaScript, 0x0b05 /* ORIYA LETTER A */],
    //  [	InternalFontScript.kTQTurkishScript,			0x0130 /* Idotaccent */	],
    //Added entries for Pali Scripts
    [InternalFontScript.kTQThaiScript, 0x0e01 /* Thai character KO KAI */],
    [InternalFontScript.kTQKhmerScript, 0x1780 /* Khmer character KA */],
    [InternalFontScript.kTQSinhaleseScript, 0x0d85 /* Sinhalese character AYANNA */],
    [InternalFontScript.kTQLaotianScript, 0x0e81 /* Lao character KO */],
    [InternalFontScript.kTQBurmeseScript, 0x1000 /* Burmese character KA */],
    [InternalFontScript.kTQEmojiScript, 0x263a /* Smiling face Emoji */],
    [InternalFontScript.kTQRomanScript, 0x00ea /* ecircumflex */],
    /* We have been told that there aren't any vietnamese fonts. are there?? */
    //   [	InternalFontScript.kTQVietnameseScript,		0x20ab /* dong */	],
    [InternalFontScript.kTQUnknownScript, 0x0000],
]);

/**
 * The class that works as a font parser for TQWeb.
 * Input in the constructor must be a valid arraybuffer.
 *
 */
export class TQFontParser {
    private _font: Font | undefined = undefined;
    private _charSet = new Set<number>();
    private _nameTableLangs = new Set<tqtypes.Language>();

    constructor(array: Uint8Array) {
        try {
            this.font = create(array);
            if (this.font.fonts && this.font.fonts.length) {
                this.font = this.font.fonts[0];
            }
        } catch (e) {
            // ..
        }
    }

    public get charSet(): Set<number> {
        if (this.font && this._charSet.size == 0 && this.font.characterSet) {
            this._charSet = new Set(this.font.characterSet);
        }
        return this._charSet;
    }

    public set charSet(value: Set<number>) {
        this._charSet = value;
    }

    public get font(): Font | undefined {
        return this._font;
    }

    public set font(value: Font | undefined) {
        this._font = value;
    }

    /**
     *
     * @returns a valid TQFont object that is created from the metafata of the parsed fontbuffer
     */
    public getTQFont() {
        const tqFont = new TQFont();
        try {
            if (this.font) {
                const nameTable = this.font["name"];
                if (this.font.postscriptName) {
                    tqFont.postscriptName = this.font.postscriptName;
                }
                if (nameTable) {
                    this.parseNameTableAttributes(nameTable, tqFont);
                }
                if (this.charSet) {
                    tqFont.characterSet = this.charSet;
                }
                this.getInternalScriptSetFromNameTable(nameTable);
            }
        } catch (e) {
            // ...
        }
        return tqFont;
    }

    // ======================= Private methods ================================
    /**
     * @param nameTable The name table record obtained from the fontkit type
     * @param tqFont A TQFont that needs to be populated
     */
    private parseNameTableAttributes(nameTable: any, tqFont: TQFont) {
        try {
            const populateNameTableRecord = (attribute: string, recordName: string, region: tqtypes.Language, fallbackRegion: tqtypes.Language) => {
                if ((attribute == "" || attribute == null) && nameTable.records[recordName]) {
                    if (nameTable.records[recordName][region] || region == fallbackRegion) {
                        attribute = nameTable.records[recordName][region];
                    } else if (nameTable.records[recordName][fallbackRegion]) {
                        attribute = nameTable.records[recordName][fallbackRegion];
                    }

                    if (attribute == "" || attribute == null) {
                        // Try others
                        for (const entry in nameTable.records[recordName]) {
                            attribute = nameTable.records[recordName][entry];
                            if (attribute != "" && attribute != null && attribute.length) {
                                break;
                            }
                        }
                    }
                }
                return attribute;
            };
            const region = TQUtils.getAppropriateRegion();
            const fallbackRegion = tqtypes.Language.Default;
            if (nameTable && nameTable.records) {
                tqFont.postscriptName = populateNameTableRecord(tqFont.postscriptName, "postscriptName", region, fallbackRegion);

                tqFont.familyName = populateNameTableRecord(tqFont.familyName, "preferredFamily", region, fallbackRegion);
                tqFont.familyName = populateNameTableRecord(tqFont.familyName, "fontFamily", region, fallbackRegion);

                tqFont.styleName = populateNameTableRecord(tqFont.styleName, "preferredSubfamily", region, fallbackRegion);
                tqFont.styleName = populateNameTableRecord(tqFont.styleName, "fontSubfamily", region, fallbackRegion);

                tqFont.fontName = tqFont.familyName + " " + tqFont.styleName;
            }
        } catch (e) {
            // ...
        }
    }

    private getInternalScriptSetFromNameTable(nameTable: any): void {
        try {
            for (const record in nameTable.records) {
                for (const recordDetails in nameTable.records[record]) {
                    const langString = recordDetails.substring(0, 2);
                    try {
                        if (isNaN(+langString)) {
                            this._nameTableLangs.add(TQUtils.getLanguageFromString(recordDetails));
                            this._nameTableLangs.add(TQUtils.getLanguageFromString(langString));
                        }
                    } catch (e) {
                        // ...
                    }
                }
            }
        } catch (e) {
            // ...
        }
    }

    private mapCmapToScript(platformId: number, platformEncoding: number): InternalFontScript {
        let script = InternalFontScript.kTQUnknownScript;
        const enc = platformEncoding as InternalFontScript;
        switch (platformId) {
            case 1: //Mac
                {
                    if (enc < InternalFontScript.kTQScriptCount) {
                        script = platformEncoding as InternalFontScript;
                    }
                }
                break;
            case 3: //Windows
                {
                    switch (platformEncoding) {
                        case 2:
                            script = InternalFontScript.kTQJapaneseScript;
                            break;
                        case 3:
                            script = InternalFontScript.kTQSimplifiedChineseScript;
                            break;
                        case 4:
                            script = InternalFontScript.kTQTraditionalChineseScript;
                            break;
                        case 5:
                        case 6:
                            script = InternalFontScript.kTQKoreanScript;
                            break;
                    }
                }
                break;
            case 0:
            default:
                script = InternalFontScript.kTQUnknownScript;
        }
        return script;
    }

    // ====================== Codepage range functions ===========================
    private readUint32FromStream(os2Table: any, tableOffset: number, offset: number) {
        const startOffset = tableOffset + offset;
        const endOffset = tableOffset + offset + 4;
        let uint32be = 0;
        try {
            const readBytes = this._font?.stream.buffer.buffer.slice(startOffset, endOffset);
            const dataview = new DataView(readBytes, 0);
            uint32be = dataview.getUint32(0); // second parameter absent or falsey == want big endian
        } catch (e) {
            // ...
        }
        return uint32be;
    }

    private readUint16FromStream(os2Table: any, tableOffset: number, offset: number) {
        const startOffset = tableOffset + offset;
        const endOffset = tableOffset + offset + 2;
        let uint16be = 0;
        try {
            const readBytes = this._font?.stream.buffer.buffer.slice(startOffset, endOffset);
            const dataview = new DataView(readBytes, 0);
            uint16be = dataview.getUint16(0); // second parameter absent or falsey == want big endian
        } catch (e) {
            // ...
        }
        return uint16be;
    }

    private OS2IsCodePageRangeBitSet(os2Table: any, bitNum: number): boolean {
        if (os2Table._length < 82) {
            return false;
        }

        const tableOffSet = os2Table._startOffset;
        const offset = 78 + Math.floor(bitNum / 32) * kInt32Size;
        const rangeField = this.readUint32FromStream(os2Table, tableOffSet, offset);
        // let bytes = [128, 129, 130, 131];
        // let uint8bytes = Uint8Array.from(bytes);
        //let int32le = dataview.getInt32(0, true); // second parameter truethy == want little endian
        return (rangeField & (1 << bitNum % 32)) != 0 ? true : false;
    }

    private TestBit(os2Table: any, bit: number): boolean {
        return this.OS2IsCodePageRangeBitSet(os2Table, bit);
    }

    private _TestRoman(os2: any): boolean {
        return this.TestBit(os2, 0) || this.TestBit(os2, 52) || this.TestBit(os2, 54) || this.TestBit(os2, 55) || this.TestBit(os2, 58) || this.TestBit(os2, 62) || this.TestBit(os2, 63);
    }

    private _TestEasternEurope(os2: any): boolean {
        return this.TestBit(os2, 1);
    }

    private _TestCyrillic(os2: any): boolean {
        return this.TestBit(os2, 2) || this.TestBit(os2, 49) || this.TestBit(os2, 57);
    }

    private _TestGreek(os2: any): boolean {
        return this.TestBit(os2, 3) || this.TestBit(os2, 48) || this.TestBit(os2, 60);
    }

    private _TestHebrew(os2: any): boolean {
        return this.TestBit(os2, 5) || this.TestBit(os2, 53);
    }

    private _TestArabic(os2: any): boolean {
        return this.TestBit(os2, 6) || this.TestBit(os2, 51) || this.TestBit(os2, 61);
    }

    private _TestThai(os2: any): boolean {
        return this.TestBit(os2, 16);
    }

    private _TestJapan(os2: any): boolean {
        return this.TestBit(os2, 17);
    }

    private _TestChineseSimplified(os2: any): boolean {
        return this.TestBit(os2, 18);
    }

    private _TestKorean(os2: any): boolean {
        return this.TestBit(os2, 19) || this.TestBit(os2, 21);
    }

    private _TestChineseTraditional(os2: any): boolean {
        return this.TestBit(os2, 20);
    }

    // ====================== unicode functions ===========================
    private OS2IsUnicodeRangeBitSet(os2Table: any, bitNum: number): boolean {
        if (os2Table._length < 46) {
            return false;
        }
        const tableOffSet = os2Table._startOffset;
        const offset = 42 + Math.floor(bitNum / 32) * kInt32Size;
        const rangeField = this.readUint32FromStream(os2Table, tableOffSet, offset);
        return (rangeField & (1 << bitNum % 32)) != 0 ? true : false;
    }

    private UniBit(os2Table: any, bit: number): boolean {
        return this.OS2IsUnicodeRangeBitSet(os2Table, bit);
    }

    private TEST_HANGUL_COMPATIBILITY_JAMO(map: any) {
        return this.UniBit(map, 52);
    }

    private TEST_HANGUL(map: any) {
        return this.UniBit(map, 56);
    }

    private TEST_BOPOMOFO(map: any) {
        return this.UniBit(map, 51);
    }

    private TEST_UNIFIED_IDEOGRAPHS(map: any) {
        return this.UniBit(map, 59);
    }

    private TEST_COMPATIBILITY_IDEOGRAPHS(map: any) {
        return this.UniBit(map, 61);
    }

    private TEST_CJK_COMPATIBILITY_FORMS(map: any) {
        return this.UniBit(map, 65);
    }

    private TEST_CJK_SYMBOLS_AND_PUNCTUATION(map: any) {
        return this.UniBit(map, 48);
    }

    private TEST_HIRAGANA(map: any) {
        return this.UniBit(map, 49);
    }

    private TEST_KATAKANA(map: any) {
        return this.UniBit(map, 50);
    }

    private IS_CJK(script: InternalFontScript) {
        return (
            script == InternalFontScript.kTQJapaneseScript ||
            script == InternalFontScript.kTQTraditionalChineseScript ||
            script == InternalFontScript.kTQSimplifiedChineseScript ||
            script == InternalFontScript.kTQKoreanScript
        );
    }

    private GetScriptUsingcmapTable(cmapTableData: any) {
        let unicodeSubtable: any = undefined;
        let script = InternalFontScript.kTQUnknownScript;
        let macPlatformScript = InternalFontScript.kTQUnknownScript;
        let msPlatformScript = InternalFontScript.kTQUnknownScript;
        if (cmapTableData) {
            let macPlatformScriptCount = 0;
            let msPlatformScriptCount = 0;
            const cmapSubtablesCount = cmapTableData.numSubtables;
            if (cmapSubtablesCount == 1) {
                const subtable = cmapTableData.tables[0];
                script = this.mapCmapToScript(subtable.platformID, subtable.encodingID);

                /* check for the unicode case */
                if (script == InternalFontScript.kTQUnknownScript && (subtable.platformID == 0 || (subtable.platformID == 3 && subtable.encodingID == 1))) {
                    if (unicodeSubtable == undefined) {
                        unicodeSubtable = cmapTableData.tables[0];
                    }
                }
            } else {
                let isDirectEncoding = false;
                for (let cmapCount = 0; cmapCount < cmapSubtablesCount; cmapCount++) {
                    const subtable = cmapTableData.tables[cmapCount];
                    const enc = subtable.platformID as InternalFontScript;
                    if (
                        subtable.platformID == 1 /*	Macintosh platform	*/ &&
                        (enc == InternalFontScript.kTQJapaneseScript ||
                            enc == InternalFontScript.kTQTraditionalChineseScript ||
                            enc == InternalFontScript.kTQKoreanScript ||
                            enc == InternalFontScript.kTQHebrewScript ||
                            enc == InternalFontScript.kTQArabicScript ||
                            enc == InternalFontScript.kTQSimplifiedChineseScript)
                    ) {
                        macPlatformScript = subtable.encodingID as InternalFontScript;
                        macPlatformScriptCount++;
                    }

                    if (subtable.platformID == 3) {
                        /* Microsoft platform	*/
                        if (subtable.encodingID == 2) {
                            /* direct ShiftJIS	*/
                            msPlatformScript = InternalFontScript.kTQJapaneseScript;
                            isDirectEncoding = true;
                            msPlatformScriptCount++;
                        } else if (subtable.encodingID == 4) {
                            /* direct Big5:	Traditional chars--Taiwan and Hong Kong*/
                            msPlatformScript = InternalFontScript.kTQTraditionalChineseScript;
                            isDirectEncoding = true;
                            msPlatformScriptCount++;
                        } else if (subtable.encodingID == 3) {
                            /* direct Chinese: Simplified chars--PRC and Singapore	*/
                            msPlatformScript = InternalFontScript.kTQSimplifiedChineseScript;
                            isDirectEncoding = true;
                            msPlatformScriptCount++;
                        } else if (subtable.encodingID == 5 || subtable.encodingID == 6) {
                            /* direct Korean Wansung or Korean Johab	*/
                            msPlatformScript = InternalFontScript.kTQKoreanScript;
                            isDirectEncoding = true;
                            msPlatformScriptCount++;
                        } else if (subtable.encodingID == 1) {
                            if (unicodeSubtable == undefined) {
                                unicodeSubtable = cmapTableData.tables[cmapCount];
                            }
                        }
                    } else if (subtable.platformID == 0) {
                        /*	Unicode platform	*/
                        if (unicodeSubtable == undefined) {
                            unicodeSubtable = cmapTableData.tables[cmapCount];
                        }
                    }
                }
                // if we found more than one platform cmap table, we can't set the writing script
                // based on any of them, unless both platforms have one for each and they match
                if (macPlatformScriptCount <= 1 && msPlatformScriptCount <= 1) {
                    if (msPlatformScriptCount == 0) {
                        script = macPlatformScript;
                    } else if (macPlatformScriptCount == 0 || macPlatformScript == msPlatformScript) {
                        script = msPlatformScript;
                    }
                }

                if (isDirectEncoding && script == InternalFontScript.kTQUnknownScript) {
                    if (this.IS_CJK(macPlatformScript)) {
                        script = macPlatformScript;
                    } else if (this.IS_CJK(msPlatformScript)) {
                        script = msPlatformScript;
                    }
                }
            }
        }
        return { script: script, unicodeSubtable: unicodeSubtable };
    }

    private GetWritingScriptFromCodePageRange(os2Tab: any) {
        let script = InternalFontScript.kTQUnknownScript;
        let numCodePages = 0;
        {
            if (this._TestKorean(os2Tab)) {
                numCodePages++;
                if (script == InternalFontScript.kTQUnknownScript) {
                    script = InternalFontScript.kTQKoreanScript;
                }
            }
            if (this._TestJapan(os2Tab)) {
                numCodePages++;
                if (script == InternalFontScript.kTQUnknownScript) {
                    script = InternalFontScript.kTQJapaneseScript;
                }
            }
            if (this._TestChineseSimplified(os2Tab)) {
                numCodePages++;
                if (script == InternalFontScript.kTQUnknownScript) {
                    script = InternalFontScript.kTQSimplifiedChineseScript;
                }
            }
            if (this._TestChineseTraditional(os2Tab)) {
                numCodePages++;
                if (script == InternalFontScript.kTQUnknownScript) {
                    script = InternalFontScript.kTQTraditionalChineseScript;
                }
            }
        }

        if (this._TestEasternEurope(os2Tab)) {
            numCodePages++;
            if (script == InternalFontScript.kTQUnknownScript) {
                script = InternalFontScript.kTQEastEuropeanRomanScript;
            }
        }
        if (this._TestCyrillic(os2Tab)) {
            numCodePages++;
            if (script == InternalFontScript.kTQUnknownScript) {
                script = InternalFontScript.kTQCyrillicScript;
            }
        }
        if (this._TestGreek(os2Tab)) {
            numCodePages++;
            if (script == InternalFontScript.kTQUnknownScript) {
                script = InternalFontScript.kTQGreekScript;
            }
        }
        if (this._TestHebrew(os2Tab)) {
            numCodePages++;
            if (script == InternalFontScript.kTQUnknownScript) {
                script = InternalFontScript.kTQHebrewScript;
            }
        }
        if (this._TestArabic(os2Tab)) {
            numCodePages++;
            if (script == InternalFontScript.kTQUnknownScript) {
                script = InternalFontScript.kTQArabicScript;
            }
        }
        if (this._TestThai(os2Tab)) {
            numCodePages++;
            if (script == InternalFontScript.kTQUnknownScript) {
                script = InternalFontScript.kTQThaiScript;
            }
        }
        if (this._TestRoman(os2Tab)) {
            numCodePages++;
            if (script == InternalFontScript.kTQUnknownScript) {
                script = InternalFontScript.kTQRomanScript;
            }
        }

        // If the script is EE or Greek, we simply set it to Roman.
        if (script == InternalFontScript.kTQEastEuropeanRomanScript || script == InternalFontScript.kTQGreekScript) {
            script = InternalFontScript.kTQRomanScript;
        }
        return { script: script, numCodePages: numCodePages };
    }

    /*	index should be set to 0 for the first call	*/
    private GetWritingScriptFromUniCMap(uniCMap: any) {
        let writingScript = InternalFontScript.kTQUnknownScript;
        if (uniCMap) {
            for (const pair of gScriptUnicodeList) {
                let uniStr = pair[1];
                if (this.charSet.has(uniStr)) {
                    if (uniStr == 0x010c) {
                        /* LS 6/2000. Some old Bitstream fonts that should be kCTRomanScript include the Ccaron character but
                            not ncaron. Do a special check for ncaron here to eliminate them.
                            */
                        uniStr = 0x0148;
                        if (this.charSet.has(uniStr)) {
                            /* Have both Ccaron and ncaron. Must be kCTEastEuropeanRomanScript, but could also have full
                                Roman support. In this case, prefer kCTRomanScript to kCTEastEuropeanRomanScript. */
                            uniStr = 0x00ea;
                            if (this.charSet.has(uniStr)) {
                                writingScript = InternalFontScript.kTQRomanScript;
                            } else {
                                writingScript = pair[0];
                            }
                        }
                    } else {
                        writingScript = pair[0];
                        if (writingScript == InternalFontScript.kTQEmojiScript) {
                            // Do a deep check
                            let count = 1;
                            const emojis = [0x2639, 0x2614, 0x26bd, 0x267b, 0x26c8];
                            for (const emoji of emojis) {
                                if (this.charSet.has(emoji)) {
                                    count = count + 1;
                                }
                            }

                            if (count < 2) {
                                writingScript = InternalFontScript.kTQUnknownScript;
                                continue;
                            }
                        }
                    }
                }

                if (writingScript != InternalFontScript.kTQUnknownScript) {
                    /*	Special check for Roman fonts (like Hoefler-Italic, file name "Hoefler.ttf")
                        that support both Roman charset and EastEuropean charset. If this font supports EE,
                        checks to see if it also has ecircumflex. If so, regard it as Roman font, not EE font.
                        */
                    if (writingScript == InternalFontScript.kTQEastEuropeanRomanScript) {
                        uniStr = 0x00ea;
                        if (this.charSet.has(uniStr)) {
                            writingScript = InternalFontScript.kTQRomanScript;
                        }
                    }
                    break;
                }
            }
        }

        return writingScript;
    }

    // returns true if the Unicode cmap table has a representative character
    // for the script, or the writing script is not listed in gScriptUnicodeList (i.e., don't know)
    private TestUnicodecmapSupportsScript(uniCMap: any, writingScript: InternalFontScript) {
        if (!uniCMap) {
            return true;
        }

        let charFound = false;
        let scriptFound = false;
        for (const pair of gScriptUnicodeList) {
            if (pair[0] == writingScript) {
                scriptFound = true;
                const uniStr = pair[1];
                if (this.charSet.has(uniStr)) {
                    charFound = true;
                    break;
                }
            }
        }
        return charFound || !scriptFound;
    }

    private OS2GetNthCodePageRangeULong(OS2Table: any, n: number) {
        if (n >= 2) {
            // Max Num CodePageRanges
            return 0;
        }
        const tableOffSet = OS2Table._startOffset;
        return this.readUint32FromStream(OS2Table, tableOffSet, 78 + n * 4);
    }

    private OS2GetVendorID(OS2Table: any) {
        if (OS2Table._length < 62) {
            return 0;
        }
        const tableOffSet = OS2Table._startOffset;
        return this.readUint32FromStream(OS2Table, tableOffSet, 58);
    }

    private OS2GetFSSelection(OS2Table: any, selection: any) {
        if (OS2Table._length < 64 || selection == undefined) {
            return { status: false, selection: selection };
        }

        const tableOffSet = OS2Table._startOffset;
        const ret = this.readUint16FromStream(OS2Table, tableOffSet, 62);
        selection = ret; // FSSelection offset = 62
        return { status: true, selection: selection };
    }

    private GetScriptUsingOS2Table(postscriptName: string, os2Stream: any, os2TableLen: number, unicodeSubtable: any) {
        let script = InternalFontScript.kTQUnknownScript;
        if (os2Stream == undefined || os2TableLen == 0) {
            return script;
        }

        if (os2Stream) {
            const adbeID = ("A".charCodeAt(0) << 24) | ("D".charCodeAt(0) << 16) | ("B".charCodeAt(0) << 8) | "E".charCodeAt(0);
            const adbeIDAlt = ("A".charCodeAt(0) << 24) | ("D".charCodeAt(0) << 16) | ("B".charCodeAt(0) << 8) | "O".charCodeAt(0);
            const msID = ("M".charCodeAt(0) << 24) | ("S".charCodeAt(0) << 16) | (" ".charCodeAt(0) << 8) | " ".charCodeAt(0);
            const vendorId = this.OS2GetVendorID(os2Stream);
            if (vendorId == adbeID || vendorId == msID || vendorId == adbeIDAlt) {
                // Handle some (e.g. myriad) adobe arabic & hebrew fonts
                if (postscriptName.indexOf("Arabic") != -1) {
                    script = InternalFontScript.kTQArabicScript;
                } else if (postscriptName.indexOf("Hebrew") != -1) {
                    script = InternalFontScript.kTQHebrewScript;
                }
            }

            if (script == InternalFontScript.kTQUnknownScript) {
                const ret = this.GetWritingScriptFromCodePageRange(os2Stream);
                const numCodePages = ret.numCodePages;
                script = ret.script;

                if (
                    script == InternalFontScript.kTQRomanScript &&
                    (this.OS2GetNthCodePageRangeULong(os2Stream, 0) == 1 /*Latin 1*/ || this.OS2GetNthCodePageRangeULong(os2Stream, 0) == 0x20000001) /*Brahmic Scripts */ &&
                    this.OS2GetNthCodePageRangeULong(os2Stream, 1) == 0
                ) {
                    script = InternalFontScript.kTQUnknownScript;
                }

                if (this.OS2GetNthCodePageRangeULong(os2Stream, 0) == 0x20000111 /*Khmer Script */) {
                    script = InternalFontScript.kTQUnknownScript;
                }

                // TODO: needs access to Unicode cmap table and glyph access.

                // if more than one codepage range are set, and the font has a Unicode cmap table,
                // then make sure the font has a representative character for the writing script
                // derived from the codepage range bits.
                // if the character is missing, then distrust the result and fall back on other methods.
                // this works around a problem with Arabic font Baghdad on Leopard 9A321
                // which is an Arabic only font but has Cyrillic and many other codepage range bits set
                if (script != InternalFontScript.kTQUnknownScript && numCodePages > 1 && unicodeSubtable) {
                    if (!this.TestUnicodecmapSupportsScript(unicodeSubtable, script)) {
                        script = InternalFontScript.kTQUnknownScript;
                    }
                }

                // Try unicodeRange bits (currently only for CJK)
                // Pranay: In case we know it's CJK, let's see there is mismatch in the name table to get more clear direction
                if (this.IS_CJK(script) && this._nameTableLangs.size == 1 && this._nameTableLangs.has(tqtypes.Language.Default)) {
                    // The one that we one is good enough
                } else if (
                    script == InternalFontScript.kTQUnknownScript ||
                    (script == InternalFontScript.kTQJapaneseScript && false == this._nameTableLangs.has(tqtypes.Language.Japanese)) ||
                    (script == InternalFontScript.kTQSimplifiedChineseScript && false == this._nameTableLangs.has(tqtypes.Language.SimplifiedChinese)) ||
                    (script == InternalFontScript.kTQTraditionalChineseScript && false == this._nameTableLangs.has(tqtypes.Language.TraditionalChinese)) ||
                    (script == InternalFontScript.kTQKoreanScript && false == this._nameTableLangs.has(tqtypes.Language.Korean))
                ) {
                    // according to Ken Lunde(lunde@adobe.com), a font whose glyph coverage supports only the
                    // characters that corresponds to Bit 52 "Hangul Compatibility Jamo" is unusable as a
                    // Korean font. So check only for bit 56.
                    if (this.TEST_HANGUL(os2Stream)) {
                        script = InternalFontScript.kTQKoreanScript;
                    } else if (this.TEST_BOPOMOFO(os2Stream)) {
                        /* Chinese must have at least the Bopomofo bit set: */
                        let count = 0;
                        if (this.TEST_UNIFIED_IDEOGRAPHS(os2Stream)) {
                            count++;
                        }
                        if (this.TEST_COMPATIBILITY_IDEOGRAPHS(os2Stream)) {
                            count++;
                        }
                        if (this.TEST_CJK_COMPATIBILITY_FORMS(os2Stream)) {
                            count++;
                        }

                        if (count >= 2) {
                            script = InternalFontScript.kTQTraditionalChineseScript;
                        } else {
                            script = InternalFontScript.kTQSimplifiedChineseScript;
                        }

                        if (false == this._nameTableLangs.has(tqtypes.Language.TraditionalChinese) && true == this._nameTableLangs.has(tqtypes.Language.SimplifiedChinese)) {
                            script = InternalFontScript.kTQSimplifiedChineseScript;
                        }
                    } else if (
                        /* TEST_CJK_SYMBOLS_AND_PUNCTUATION(os2Table) || TEST_UNIFIED_IDEOGRAPHS(os2Table) || */
                        this.TEST_HIRAGANA(os2Stream) ||
                        this.TEST_KATAKANA(os2Stream)
                    ) {
                        // Check for Japanese AppleSymbols on Snow Leopard (10.6) contains ideographs and has the Unified ideographs bit set.
                        // Don't treat it as a Japanese font.
                        script = InternalFontScript.kTQJapaneseScript;
                    }
                }

                /* Check for Windows 3.1 Arabic fonts in the uppper byte of fsSelection
                 (undocumented in OT spec 1.4!) */
                let selection = 0;
                if (script == InternalFontScript.kTQUnknownScript) {
                    const ret = this.OS2GetFSSelection(os2Stream, selection);
                    if (ret.status) {
                        selection = ret.selection;
                        selection >>= 8;
                        switch (selection) {
                            case 0xb2 /* simplified Arabic */:
                            case 0xb3 /* traditional Arabic */:
                                script = InternalFontScript.kTQArabicScript;
                                break;
                            case 0xb1 /* hebrew */:
                                script = InternalFontScript.kTQHebrewScript;
                                break;
                        }
                    }
                }
            }
        }
        /*	Another desperate attempt. This is a TrueType font whith a unicode cmap but
         it has none of the unicode or code page range tables in the OS/2 table.
         (Actually, we've only checked unicode ranges for CJK, so the above is not quite
         true.)
         check a code point representative for each script in the unicode cmap to determine
         the writing script of the font. Windows system appears to be doing this also.	*/
        if (script == InternalFontScript.kTQUnknownScript && unicodeSubtable) {
            script = this.GetWritingScriptFromUniCMap(unicodeSubtable);
        }

        return script;
    }

    private computeFontScriptInternal(postscriptName: string): InternalFontScript {
        if (postscriptName == "") {
            return InternalFontScript.kTQUnknownScript;
        }

        const lowerPsName = postscriptName.toLocaleLowerCase();
        if (lowerPsName.indexOf("color") != -1 && lowerPsName.indexOf("emoji") != -1) {
            // TODO: Pranay, need to revisit this
            return InternalFontScript.kTQEmojiScript;
        }

        /*
        CoolType heuristics to fetch writing script in sequence.
        This has been derived from Method ATMCGetWritingScript() in file ATMCore.cpp

        1. Ask MACOS to get writing script using fontURL.
        2. Writing script for Plain Type1 fonts.
        3. Script for CID - CFF fonts using Registry and Ordering
        4. Use of cmap table to compute script before going to OS/2 table.
        5. Script determination using OS/2 table.
        6. NIS Japanese fonts don't have anything of the above information.
        As a desperate effort, check the name table for one of the CJK font names.
        7. Another desperate attempt. This is a TrueType font whith a unicode cmap but
        it has none of the unicode or code page range tables in the OS/2 table.
        (Actually, we've only checked unicode ranges for CJK, so the above is not quite
        true.)
        check a code point representative for each script in the unicode cmap to determine
        the writing script of the font. Windows system appears to be doing this also.
        8. Only on win, using lfCharSet.
        */

        let primaryScript = InternalFontScript.kTQUnknownScript; // Start with unknown script.

        // 3. Check Registry and Ordering from CFF table if it's CID CFF font.
        // TODO: It required parsing of CFF table.

        // 4. Identify script using cmap table.
        let unicodeSubtable: any;
        if (primaryScript == InternalFontScript.kTQUnknownScript) {
            // Fetch cmap table.
            if (this.font && this.font["cmap"]) {
                const cmapTable = this.font["cmap"];
                const ret = this.GetScriptUsingcmapTable(cmapTable);
                unicodeSubtable = ret.unicodeSubtable;
                primaryScript = ret.script;
            }
        }

        // 5. 6. 7. Check various fields in the OS/2 table.
        if (primaryScript == InternalFontScript.kTQUnknownScript || primaryScript == InternalFontScript.kTQRomanScript) {
            if (this.font && (this.font as any)["OS/2"]) {
                const os2Table = (this.font as any)["OS/2"];
                const os2script = this.GetScriptUsingOS2Table(postscriptName, (this.font as any)["OS/2"], os2Table._length, unicodeSubtable);
                if (os2script != InternalFontScript.kTQUnknownScript) {
                    primaryScript = os2script;
                }
            }
        }
        return primaryScript;
    }

    public computeFontScript(postscriptName: string): tqtypes.Language {
        try {
            TQInitParams.Logger.logMessage(TQLogLevel.DEBUG, this._font);
            return ConvertInternalFontScriptToLanguage(this.computeFontScriptInternal(postscriptName));
        } catch (e) {
            return tqtypes.Language.Default;
        }
    }

    public getFVarOrdering() {
        const axisOrderingMap = new Map<string, number>();
        try {
            let number = 0;
            if (this._font && this._font.fvar && this._font.fvar.axis) {
                for (const axisEntry of this._font.fvar.axis) {
                    axisOrderingMap.set(axisEntry.axisTag, number);
                    number = number + 1;
                }
            }
        } catch (e) {
            // ...
        }
        return new Map(
            [...axisOrderingMap].sort((a, b) => {
                return a[1] - b[1];
            })
        );
    }

    public parseStatTable() {
        const axisOrderingMap = new Map<string, number>();
        try {
            if (this._font && this._font.directory.tables && this._font.directory.tables.STAT) {
                const axisCount = this.readUint16FromStream(this._font.stream, this._font.directory.tables.STAT.offset, 6);
                let axisOffset = this.readUint32FromStream(this._font.stream, this._font.directory.tables.STAT.offset, 8);
                for (let count = 0; count < axisCount; count++) {
                    const axisTagNum = this.readUint32FromStream(this._font.stream, this._font.directory.tables.STAT.offset, axisOffset);
                    const axisTagArray = TQUtils.toBytesInt32(axisTagNum);
                    const axisTag = String.fromCharCode.apply(null, axisTagArray as any);
                    axisOffset = axisOffset + 6;
                    const axisTagOrdering = this.readUint16FromStream(this._font.stream, this._font.directory.tables.STAT.offset, axisOffset);
                    axisOffset = axisOffset + 2;
                    axisOrderingMap.set(axisTag, axisTagOrdering);
                }
            }
        } catch (e) {
            // ...
        }
        return new Map(
            [...axisOrderingMap].sort((a, b) => {
                return a[1] - b[1];
            })
        );
    }
}
